import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, Phone, Mail, MapPin, Facebook, Instagram,
  CalendarCheck, HardHat, LayoutDashboard, Menu, X as CloseIcon,
} from "lucide-react";
import { captureUtmFromUrl } from "@/lib/utm";
import LiveCounter from "@/components/LiveCounter";
import HurricaneBanner from "@/components/HurricaneBanner";
import { PowerBuiltMark } from "@/components/PowerBuiltMark";
import SEO from "@/components/SEO";

const HERO_IMG = "/landing-hero.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: "Services",     to: "#services",         hash: true, testid: "nav-services" },
  { label: "How It Works", to: "#how-it-works",     hash: true, testid: "nav-how" },
  { label: "About",        to: "/about",            hash: false, testid: "nav-about" },
  { label: "Contact",      to: "/contact",          hash: false, testid: "nav-contact" },
];

function BrandWordmark({ compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
      <PowerBuiltMark className="w-11 h-11 shrink-0 transition-transform group-hover:rotate-3" />
      <div className="leading-tight">
        <div className="font-heading font-semibold tracking-wide text-[15px] text-white">
          PowerBuilt
        </div>
        {!compact && (
          <div className="text-white/50 text-[10px] tracking-[0.28em] uppercase">
            Solar · Electrical · Maintenance
          </div>
        )}
      </div>
    </Link>
  );
}

export default function LandingPage() {
  const [settings, setSettings] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    captureUtmFromUrl();
    axios.get(`${API}/site-settings/public`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const heroVideo = settings?.hero_video_enabled && settings?.hero_video_url;

  const scrollToId = (e, id) => {
    e.preventDefault();
    setMobileNavOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#0A2540] text-white relative overflow-hidden">
      <SEO
        path="/"
        title="PowerBuilt — Solar · Electrical · Maintenance · Sint Maarten"
        description="Solar, electrical and system maintenance contractor in Sint Maarten (St. Maarten). Rooftop solar, battery storage, licensed electrical work and ongoing service — one PowerBuilt account, end to end."
      />
      <HurricaneBanner />

      {/* Ambient brand gradients — gold sun top-left, gold sunset right, green solar-panel glow bottom */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C9A356]/12 blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#C9A356]/8 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-40 left-1/4 w-[420px] h-[420px] rounded-full bg-[#8CDC5A]/8 blur-3xl pointer-events-none"></div>

      {/* Top nav */}
      <header className="relative z-20 max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <BrandWordmark />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="landing-nav">
          {NAV_LINKS.map((n) => (
            n.hash ? (
              <a
                key={n.to}
                href={n.to}
                onClick={(e) => scrollToId(e, n.to.slice(1))}
                data-testid={n.testid}
                className="text-sm text-white/70 hover:text-white transition"
              >
                {n.label}
              </a>
            ) : (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className="text-sm text-white/70 hover:text-white transition"
              >
                {n.label}
              </Link>
            )
          ))}
          <Link
            to="/login"
            data-testid="nav-signin"
            className="text-sm font-medium text-white/80 hover:text-white transition"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            data-testid="nav-getstarted"
            className="btn-accent text-sm px-5 py-2"
          >
            Get Started
          </Link>
        </nav>

        {/* Mobile nav trigger */}
        <button
          className="lg:hidden w-10 h-10 grid place-items-center rounded-md text-white hover:bg-white/10"
          onClick={() => setMobileNavOpen((v) => !v)}
          data-testid="mobile-nav-toggle"
          aria-label="Toggle menu"
        >
          {mobileNavOpen ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {/* Mobile nav sheet */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-x-0 top-0 z-30 pt-20 bg-[#0A2540]/98 backdrop-blur-md min-h-screen" data-testid="mobile-nav-sheet">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between border-b border-white/10 -mt-16 mb-6">
            <BrandWordmark compact />
            <button
              onClick={() => setMobileNavOpen(false)}
              className="w-10 h-10 grid place-items-center rounded-md text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="max-w-6xl mx-auto px-6 flex flex-col gap-3">
            {NAV_LINKS.map((n) => (
              n.hash ? (
                <a
                  key={n.to}
                  href={n.to}
                  onClick={(e) => scrollToId(e, n.to.slice(1))}
                  className="text-lg py-3 border-b border-white/10 text-white/85 hover:text-white"
                >
                  {n.label}
                </a>
              ) : (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-lg py-3 border-b border-white/10 text-white/85 hover:text-white"
                  onClick={() => setMobileNavOpen(false)}
                >
                  {n.label}
                </Link>
              )
            ))}
            <Link
              to="/login"
              className="text-lg py-3 border-b border-white/10 text-white/85 hover:text-white"
              onClick={() => setMobileNavOpen(false)}
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="btn-accent text-base mt-4 py-3 text-center"
              onClick={() => setMobileNavOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}

      {/* Hero */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 md:pt-16 pb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="fade-in-up">
            <p className="overline text-[#C9A356] mb-4">PowerBuilt</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
              Solar. Electrical. <span className="text-[#B7F58A]">Maintenance.</span><br />
              <span className="text-[#C9A356]">Built Right.</span>
            </h1>

            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-md">
              Solar, electrical and maintenance solutions designed for St. Maarten.
            </p>
            <p className="mt-3 text-base text-slate-400 leading-relaxed max-w-md">
              From consultation to installation and after-sales service — manage your entire PowerBuilt experience from one account.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="btn-accent text-base px-7 py-3" data-testid="hero-cta-register">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to="/login"
                className="px-7 py-3 rounded-md font-medium text-white border-2 border-white/30 hover:bg-white/10 transition"
                data-testid="hero-cta-signin"
              >
                Sign In
              </Link>
            </div>
          </div>

          {/* Right side: video (if enabled) or brand image */}
          <div className="relative md:block">
            {/* Soft green solar-panel halo behind the hero */}
            <div className="absolute -inset-6 bg-[#8CDC5A]/10 blur-2xl rounded-[2.5rem] pointer-events-none"></div>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black/40">
              {heroVideo ? (
                <video
                  src={settings.hero_video_url}
                  autoPlay loop muted playsInline
                  data-testid="hero-video"
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={HERO_IMG}
                  alt="PowerBuilt solar installation"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Plan · Build · Manage — value section */}
      <section id="how-it-works" className="relative z-10 border-t border-white/10 bg-[#0A2540]/60">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10 max-w-2xl">
            <p className="overline text-[#C9A356] mb-3">How It Works</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight">
              One account. The whole journey.
            </h2>
            <p className="mt-3 text-slate-300 max-w-xl">
              PowerBuilt is a contractor and a digital platform. Every customer gets a private workspace to plan, follow and maintain their system.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="value-section">
            {[
              {
                icon: CalendarCheck,
                step: "Plan",
                title: "Plan",
                copy: "Book your consultation and receive the right solution for your property.",
                testid: "value-plan",
                accent: "gold",
              },
              {
                icon: HardHat,
                step: "Build",
                title: "Build",
                copy: "Follow your installation or project from start to finish.",
                testid: "value-build",
                accent: "gold",
              },
              {
                icon: LayoutDashboard,
                step: "Manage",
                title: "Manage",
                copy: "Pay invoices, access documents, request service and expand your system anytime.",
                testid: "value-manage",
                accent: "green",
              },
            ].map((v) => {
              const isGreen = v.accent === "green";
              const ringHover = isGreen ? "hover:border-[#8CDC5A]/50" : "hover:border-[#C9A356]/50";
              const chipBg    = isGreen ? "bg-[#8CDC5A]/15 text-[#B7F58A] group-hover:bg-[#8CDC5A]/25" : "bg-[#C9A356]/15 text-[#C9A356] group-hover:bg-[#C9A356]/25";
              const label     = isGreen ? "text-[#B7F58A]" : "text-[#C9A356]";
              return (
                <div
                  key={v.step}
                  data-testid={v.testid}
                  className={`group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition ${ringHover}`}
                >
                  <div className={`w-11 h-11 rounded-xl grid place-items-center mb-5 transition ${chipBg}`}>
                    <v.icon className="w-5 h-5" />
                  </div>
                  <div className={`text-[10px] tracking-[0.28em] uppercase mb-1 ${label}`}>Step</div>
                  <h3 className="font-heading text-xl text-white mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{v.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services grid — indexable links to service landing pages */}
      <section id="services" className="relative z-10 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10 max-w-2xl">
            <p className="overline text-[#C9A356] mb-3">Services</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight">
              What PowerBuilt handles.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Link to="/solar" data-testid="services-solar" className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50 block">
              <h3 className="font-heading text-xl text-white mb-2">Solar &amp; battery storage</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Rooftop PV, hybrid inverters and LFP batteries — designed and installed for the Caribbean climate.</p>
              <span className="text-xs text-[#C9A356] mt-4 inline-flex items-center">Learn more <ArrowRight className="w-3.5 h-3.5 ml-1" /></span>
            </Link>
            <Link to="/electrical" data-testid="services-electrical" className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50 block">
              <h3 className="font-heading text-xl text-white mb-2">Electrical work</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Licensed installations, panel upgrades, dedicated circuits and EV-ready wiring for homes and businesses.</p>
              <span className="text-xs text-[#C9A356] mt-4 inline-flex items-center">Learn more <ArrowRight className="w-3.5 h-3.5 ml-1" /></span>
            </Link>
            <Link to="/maintenance" data-testid="services-maintenance" className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#8CDC5A]/50 block">
              <h3 className="font-heading text-xl text-white mb-2">System maintenance</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Scheduled inspections, cleaning, diagnostics and repairs to keep systems generating long after handover.</p>
              <span className="text-xs text-[#B7F58A] mt-4 inline-flex items-center">Learn more <ArrowRight className="w-3.5 h-3.5 ml-1" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Anchor placeholders for future sections referenced in nav */}
      <div id="our-work" aria-hidden="true" />
      <div id="about" aria-hidden="true" />

      {/* Live impact counter — clickable, links to /impact */}
      <Link to="/impact" data-testid="live-counter-link" className="block group">
        <LiveCounter />
        <div className="max-w-6xl mx-auto px-6 -mt-4 pb-2">
          <span className="text-xs text-[#C9A356]/80 group-hover:text-[#C9A356] transition">
            See full sustainability breakdown →
          </span>
        </div>
      </Link>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 mt-12" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="mb-4">
                <BrandWordmark />
              </div>
              <p className="text-sm text-white/60 leading-relaxed max-w-md">
                Solar, electrical and maintenance services for Sint Maarten. Built to last, backed by service — one project at a time.
              </p>
            </div>

            <div>
              <p className="overline text-[#C9A356] mb-3">Contact</p>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                  <span>{settings?.address || "Cole Bay, Sint Maarten"}</span>
                </li>
                <li>
                  <a href={`tel:${(settings?.phone || "").replace(/[^+0-9]/g, "")}`} className="flex items-start gap-2 hover:text-white transition" data-testid="footer-phone">
                    <Phone className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                    <span>{settings?.phone || "+1 (721) 000-0000"}</span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${settings?.email || "hello@powerbuilt.sxm"}`} className="flex items-start gap-2 hover:text-white transition" data-testid="footer-email">
                    <Mail className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                    <span>{settings?.email || "hello@powerbuilt.sxm"}</span>
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="overline text-[#C9A356] mb-3">Follow</p>
              <div className="flex gap-2 mb-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"
                   className="w-9 h-9 rounded-md border border-white/20 grid place-items-center text-white/70 hover:text-white hover:border-[#C9A356] transition"
                   data-testid="footer-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                   className="w-9 h-9 rounded-md border border-white/20 grid place-items-center text-white/70 hover:text-white hover:border-[#C9A356] transition"
                   data-testid="footer-instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
              <div className="text-xs text-white/50 space-y-1">
                <div className="text-white/30 text-[10px]">Chamber of Commerce member</div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-white/50">
            <div>© 2026 PowerBuilt · Solar, Electrical &amp; Maintenance</div>
            <div className="flex gap-4 flex-wrap">
              <Link to="/solar"       className="hover:text-white transition">Solar</Link>
              <Link to="/electrical"  className="hover:text-white transition">Electrical</Link>
              <Link to="/maintenance" className="hover:text-white transition">Maintenance</Link>
              <Link to="/about"       className="hover:text-white transition">About</Link>
              <Link to="/contact"     className="hover:text-white transition">Contact</Link>
              <Link to="/impact"      className="hover:text-white transition">Our Impact</Link>
              <Link to="/login"       className="hover:text-white transition">Sign In</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
